import React, { useState, useEffect, useContext } from 'react';
import { LocaleContext, AuthContext, CartContext } from '../locale';
import './Checkout.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export default function Checkout() {
  const { t, locale } = useContext(LocaleContext);
  const { user, token, openAuth } = useContext(AuthContext);
  const { cart, getCartTotal, removeFromCart } = useContext(CartContext);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  
  // NEW: modal + COD state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [creatingCod, setCreatingCod] = useState(false);
  const [codSuccess, setCodSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  const subtotal = getCartTotal();
  
  // Format currency based on locale
  const nf = new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { 
    style: 'currency', 
    currency: 'EUR' 
  });

  // If no cart items, redirect to cart page
  useEffect(() => {
    if (!cart || cart.length === 0) {
      window.location.hash = '#/cart';
    }
  }, [cart]);

  // If user is not logged in, open auth modal
  useEffect(() => {
    if (!user && !loading) {
      openAuth('login');
    }
  }, [user, openAuth, loading]);

  // NEW: watch hash for COD success (e.g., #/checkout?cod=1&order_id=123)
  useEffect(() => {
    const readHash = () => {
      const h = window.location.hash || '';
      const qs = h.split('?')[1] || '';
      const params = new URLSearchParams(qs);
      setCodSuccess(params.get('cod') === '1');
      setOrderId(params.get('order_id') || null);
    };
    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  // Handle Stripe checkout (unchanged, moved into its own function)
  const startStripeCheckout = async () => {
    if (!user) {
      openAuth('login');
      return;
    }
    
    if (!token) {
      setError("Authentication required. Please log in.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: cart,
          customer_email: user.email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error creating checkout session');
      }
      
      // Redirect to Stripe Checkout
      setRedirecting(true);
      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'An error occurred during checkout');
      setLoading(false);
    }
  };

  // NEW: Handle Cash on Delivery (Paiement à la livraison)
  const handleCashOnDelivery = async () => {
    if (!user) {
      openAuth('login');
      return;
    }
    if (!token) {
      setError("Authentication required. Please log in.");
      return;
    }
    try {
      setCreatingCod(true);
      setError(null);
      const res = await fetch(`${API_BASE}/payments/cod-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: cart,
          customer_email: user.email
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Erreur lors de la création de la commande');
      }
      // Clear cart (best effort)
      (Array.isArray(cart) ? cart : []).forEach(it => removeFromCart(it.id));
      setShowPaymentModal(false);
      // Redirect to confirmation (same page, different view)
      const oid = data.orderId || data.id || '';
      window.location.hash = `#/checkout?cod=1${oid ? `&order_id=${encodeURIComponent(oid)}` : ''}`;
    } catch (e) {
      console.error('COD order error:', e);
      setError(e.message || 'Erreur');
    } finally {
      setCreatingCod(false);
    }
  };

  // Early returns when not ready
  if (!user || !cart || cart.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="checkout-loading">
            <div className="spinner"></div>
            <p>{!user ? 'Please log in to continue' : 'Your cart is empty'}</p>
          </div>
        </div>
      </div>
    );
  }

  // NEW: COD confirmation view (French)
  if (codSuccess) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="cod-confirm">
            <div className="confetti">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
            <div className="cod-card">
              <div className="cod-icon">✅</div>
              <h1>Commande confirmée</h1>
              <p className="cod-sub">
                Merci pour votre commande. Votre mode de paiement sélectionné est <strong>Paiement à la livraison</strong>.
              </p>
              {orderId ? <p className="cod-order">Numéro de commande: <strong>#{orderId}</strong></p> : null}
              <ul className="cod-details">
                <li>Nous vous enverrons un e‑mail de confirmation avec les détails de votre commande.</li>
                <li>Le paiement sera effectué à la réception de votre colis.</li>
                <li>Livraison estimée: 3 à 5 jours ouvrés.</li>
              </ul>
              <div className="cod-actions">
                <a href="#/" className="btn primary">Retour à l’accueil</a>
                <a href="#/products" className="btn secondary">Continuer mes achats</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal checkout page
  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <p>Review your order and proceed to payment</p>
        </div>
        
        {error && (
          <div className="checkout-error">
            <p>{error}</p>
          </div>
        )}
        
        <div className="checkout-content">
          <div className="checkout-items">
            <h2>Order Summary</h2>
            <div className="checkout-list">
              {cart.map((item) => (
                <div key={item.id} className="checkout-item">
                  <div className="checkout-item-image">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.name} 
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="checkout-item-fallback" style={{display: item.image_url ? 'none' : 'flex'}}>
                      {item.category === 'beer' ? '🍺' : item.category === 'wine' ? '🍷' : '🥃'}
                    </div>
                  </div>
                  <div className="checkout-item-details">
                    <h3>{item.name}</h3>
                    <div className="checkout-item-meta">
                      <span>{item.marque}</span>
                      <span className="meta-dot">•</span>
                      <span>{item.volume}</span>
                      {item.abv && (
                        <>
                          <span className="meta-dot">•</span>
                          <span>{item.abv}% vol</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="checkout-item-quantity">
                    {item.quantity} × {nf.format(item.price)}
                  </div>
                  <div className="checkout-item-price">
                    {nf.format(item.price * item.quantity)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="checkout-summary">
            <h2>Payment Summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{nf.format(subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>Calculated at next step</span>
            </div>
            <div className="summary-row">
              <span>Taxes</span>
              <span>Calculated at next step</span>
            </div>
            <div className="summary-total">
              <span>Total</span>
              <span>{nf.format(subtotal)}</span>
            </div>
            
            {/* OPEN MODAL INSTEAD OF DIRECT STRIPE */}
            <button 
              className="btn primary checkout-button" 
              onClick={() => setShowPaymentModal(true)}
              disabled={loading || redirecting}
            >
              {loading || redirecting ? (
                <>
                  <div className="spinner-small"></div>
                  {redirecting ? 'Redirecting to Stripe...' : 'Processing...'}
                </>
              ) : (
                'Proceed to Payment'
              )}
            </button>
            
            <a href="#/cart" className="checkout-back-link">
              Return to Cart
            </a>
            
            <div className="checkout-security">
              <div className="security-icon">🔒</div>
              <span>Secure payment powered by Stripe</span>
            </div>
          </div>
        </div>
      </div>

      {/* NEW: Payment choice modal */}
      {showPaymentModal && (
        <div className="payment-modal-backdrop" onClick={() => !loading && !creatingCod && setShowPaymentModal(false)}>
          <div
            className="payment-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pm-header">
              <div className="pm-icon">💳</div>
              <h3>Choisissez votre mode de paiement</h3>
              <p>Sélectionnez une option pour finaliser votre commande</p>
            </div>
            <div className="pm-options">
              <button
                className="pm-option"
                onClick={startStripeCheckout}
                disabled={loading || redirecting || creatingCod}
              >
                <div className="opt-icon">✨</div>
                <div className="opt-body">
                  <div className="opt-title">Payer en ligne</div>
                  <div className="opt-sub">Rapide, sécurisé et instantané</div>
                </div>
                <div className="opt-cta">Continuer →</div>
              </button>
              <button
                className="pm-option secondary"
                onClick={handleCashOnDelivery}
                disabled={loading || redirecting || creatingCod}
              >
                <div className="opt-icon">🚚</div>
                <div className="opt-body">
                  <div className="opt-title">Paiement à la livraison</div>
                  <div className="opt-sub">Payez en espèces/carte à la réception</div>
                </div>
                <div className="opt-cta">
                  {creatingCod ? <div className="spinner-small" /> : 'Confirmer →'}
                </div>
              </button>
            </div>
            <button
              className="pm-close"
              onClick={() => setShowPaymentModal(false)}
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
